import type {TemplateFormInput, YamlTemplate} from '@github-ui/code-view-types'
import {VerifiedHTMLBox} from '@github-ui/safe-html/VerifiedHTML'
import {UploadIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, Checkbox, CheckboxGroup, FormControl, Textarea, TextInput} from '@primer/react'
import type {PropsWithChildren} from 'react'

import styles from './YamlTemplateContent.module.css'

export const YamlTemplateType = {
  Issue: 'issue',
  Discussion: 'discussion',
} as const

export type YamlTemplateType = (typeof YamlTemplateType)[keyof typeof YamlTemplateType]

export function YamlTemplateContent({issueTemplate, type}: {issueTemplate: YamlTemplate; type: YamlTemplateType}) {
  return (
    <div className={styles.templateWrapper}>
      <table className={styles.metadataTable}>
        <thead>
          <tr>
            {type === YamlTemplateType.Issue ? (
              <>
                <MarkdownTableCell header>Name</MarkdownTableCell>
                <MarkdownTableCell header>About</MarkdownTableCell>
              </>
            ) : (
              <MarkdownTableCell header>Title</MarkdownTableCell>
            )}
            {issueTemplate.type && <MarkdownTableCell header>Type</MarkdownTableCell>}
            <MarkdownTableCell header>Labels</MarkdownTableCell>
            {issueTemplate.projects && <MarkdownTableCell header>Projects</MarkdownTableCell>}

            {type === YamlTemplateType.Issue && <MarkdownTableCell header>Assignees</MarkdownTableCell>}
          </tr>
        </thead>
        <tbody>
          <tr>
            {type === YamlTemplateType.Issue ? (
              <>
                <MarkdownTableCell>{issueTemplate.name}</MarkdownTableCell>
                <MarkdownTableCell>{issueTemplate.about}</MarkdownTableCell>
              </>
            ) : (
              <MarkdownTableCell>{issueTemplate.title}</MarkdownTableCell>
            )}
            {issueTemplate.type && <MarkdownTableCell>{issueTemplate.type}</MarkdownTableCell>}
            <MarkdownTableCell>{issueTemplate.labels}</MarkdownTableCell>
            {issueTemplate.projects && <MarkdownTableCell>{issueTemplate.projects}</MarkdownTableCell>}

            {type === YamlTemplateType.Issue && <MarkdownTableCell>{issueTemplate.assignees}</MarkdownTableCell>}
          </tr>
        </tbody>
      </table>
      {issueTemplate.inputs.map((input, index) => (
        <TemplateInput
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={index}
          input={input}
        />
      ))}
    </div>
  )
}

function MarkdownTableCell({children, header}: PropsWithChildren<{header?: boolean}>) {
  const Tag = header ? 'th' : 'td'
  return <Tag className={styles.tableCell}>{children}</Tag>
}

function TemplateInput({input}: {input: TemplateFormInput}): React.ReactElement | null {
  switch (input.type) {
    case 'markdown':
      return <MarkdownInput input={input} />
    case 'dropdown':
      return <DropdownInput input={input} />
    case 'input':
      return <InputInput input={input} />
    case 'textarea':
      return <TextareaInput input={input} />
    case 'checkboxes':
      return <CheckboxesInput input={input} />
    case 'upload':
      return <UploadInput input={input} />
    default:
      return null
  }
}

// TODO: Get html from markdown
function MarkdownInput({input}: {input: TemplateFormInput}) {
  if (!input.value) return null
  return <VerifiedHTMLBox html={input.value} />
}

function DropdownInput({input}: {input: TemplateFormInput}) {
  const options = input.options?.slice()

  if (!input.required) {
    options?.unshift('None')
  }

  let buttonText = input.multiple ? 'Selections: ' : 'Selection: '

  if (input.value) {
    buttonText += input.value
  }

  return (
    <InputWrapper input={input} style={{alignItems: 'start'}}>
      <ActionMenu>
        <ActionMenu.Button>{buttonText}</ActionMenu.Button>
        <ActionMenu.Overlay width="medium">
          <ActionList selectionVariant={input.multiple ? 'multiple' : 'single'}>
            {options?.map((option, index) => (
              <ActionList.Item
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={index}
                selected={option === input.value}
                disabled
              >
                {option}
              </ActionList.Item>
            ))}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </InputWrapper>
  )
}

function InputInput({input}: {input: TemplateFormInput}) {
  return (
    <InputWrapper input={input}>
      <TextInput placeholder={input.placeholder} value={input.value ?? ''} />
    </InputWrapper>
  )
}

/**
 * Since the blob viewer version is read only, we don't need the functionality to preserve content
 * when switching to preview and on navigation.
 */
function TextareaInput({input}: {input: TemplateFormInput}) {
  return (
    <InputWrapper input={input}>
      <Textarea
        placeholder={input.placeholder}
        value={input.value ?? ''}
        style={input.render ? {fontFamily: 'mono'} : {}}
      />
    </InputWrapper>
  )
}

function CheckboxesInput({input}: {input: TemplateFormInput}) {
  if (!input.checkboxes) {
    return null
  }

  return (
    <CheckboxGroup disabled className={styles.CheckboxGroup}>
      <CheckboxGroup.Label className={styles.CheckboxGroup_Label}>{input.label}</CheckboxGroup.Label>
      {input.description && (
        <CheckboxGroup.Caption className={styles.CheckboxGroup_Caption}>
          <VerifiedHTMLBox html={input.description} />
        </CheckboxGroup.Caption>
      )}
      {input.checkboxes.map((checkbox, index) => (
        <FormControl
          disabled
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={index}
          required={checkbox.required}
        >
          <Checkbox />
          <FormControl.Label>{checkbox.label}</FormControl.Label>
        </FormControl>
      ))}
    </CheckboxGroup>
  )
}

function UploadInput({input}: {input: TemplateFormInput}) {
  return (
    <InputWrapper input={input}>
      <Button disabled leadingVisual={UploadIcon}>
        Upload files
      </Button>
    </InputWrapper>
  )
}

function InputWrapper({
  children,
  input,
  style,
}: PropsWithChildren<{input: TemplateFormInput; style?: React.CSSProperties}>) {
  return (
    <FormControl
      disabled
      required={input.required}
      style={{
        marginLeft: '15px',
        marginRight: '15px',
        ...style,
      }}
    >
      <FormControl.Label className={styles.FormControl_Label}>{input.label}</FormControl.Label>
      {input.description && (
        <FormControl.Caption>
          <VerifiedHTMLBox html={input.description} />
        </FormControl.Caption>
      )}
      {children}
    </FormControl>
  )
}
